import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

const proButtonVariants = cva(
  "box-border inline-flex items-center justify-center gap-1.5 font-semibold rounded-[8px] border-[1.5px] cursor-pointer whitespace-nowrap transition-all duration-150 active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-lime focus-visible:outline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100",
  {
    variants: {
      variant: {
        lime: "btn-lime bg-lime text-pro-lime-text-on border-lime hover:bg-lime-dim hover:border-lime-dim",
        outline:
          "btn-outline bg-transparent text-pro-text border-pro-border hover:bg-pro-elevated hover:border-pro-text-faint",
        ghost:
          "btn-ghost bg-transparent text-pro-text-secondary border-transparent hover:bg-white/5 hover:text-pro-text",
        danger: "btn-danger bg-pro-danger text-white border-pro-danger",
      },
      size: {
        sm: "h-8 px-3 text-[12px] leading-none",
        default: "h-9 px-4 text-[13px]",
        lg: "h-11 px-6 text-[15px] rounded-[12px]",
      },
    },
    defaultVariants: { variant: "lime", size: "default" },
  },
);

interface ProButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof proButtonVariants> {
  loading?: boolean;
  asChild?: boolean;
}

function ProButton({
  className,
  variant,
  size,
  loading,
  asChild,
  disabled,
  type = "button",
  children,
  ...props
}: ProButtonProps) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      type={type}
      className={cn(proButtonVariants({ variant, size }), className)}
      disabled={loading || disabled}
      {...props}
    >
      {loading ? (
        <>
          <svg className="size-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          {children}
        </>
      ) : (
        children
      )}
    </Comp>
  );
}

export { ProButton, type ProButtonProps, proButtonVariants };
